import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { 
  Zap, 
  Target, 
  Shield,
  ArrowRight,
  Clock,
  AlertTriangle,
  Check,
  X
} from 'lucide-react'
import { clsx } from 'clsx'
import type { ActionGrade, GradeActions, GradeActionsWithScript, Action } from '@/types'
import ActionAcceptModal from './ActionAcceptModal'
import ActionRejectModal from './ActionRejectModal'

// 动作状态配置
const ACTION_STATUS_CONFIG: Record<string, {
  label: string
  color: string
  bgColor: string
}> = {
  proposed: { label: '待处理', color: 'text-gray-600', bgColor: 'bg-gray-100' },
  accepted: { label: '已采纳', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  done: { label: '已完成', color: 'text-green-700', bgColor: 'bg-green-100' },
  rejected: { label: '已拒绝', color: 'text-red-700', bgColor: 'bg-red-100' },
  not_applicable: { label: '不适用', color: 'text-gray-500', bgColor: 'bg-gray-100' },
}

interface OverviewActionPanelProps {
  diagnosisType: string
  recommendedGrade: ActionGrade
  actionsByGrade: Record<ActionGrade, GradeActions | GradeActionsWithScript>
  onViewDetails: () => void
  isLoading?: boolean
  // 动作操作回调
  onAcceptAction?: (actionId: string, scheduledDate?: string) => Promise<void>
  onRejectAction?: (actionId: string, reason?: string) => Promise<void>
  onRefresh?: () => void
  onShowToast?: (message: string, type?: 'success' | 'error' | 'info') => void
}

// 等级配置
const GRADE_CONFIG: Record<ActionGrade, {
  label: string
  icon: React.ElementType
  color: string
  bgColor: string
  borderColor: string
  textColor: string
}> = {
  A: {
    label: '最佳路径',
    icon: Zap,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-500',
    textColor: 'text-green-700',
  },
  B: {
    label: '快速验证',
    icon: Target,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-700',
  },
  C: {
    label: '止血动作',
    icon: Shield,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-500',
    textColor: 'text-orange-700',
  },
}

export default function OverviewActionPanel({
  diagnosisType,
  recommendedGrade,
  actionsByGrade,
  onViewDetails,
  isLoading = false,
  onAcceptAction,
  onRejectAction,
  onRefresh,
  onShowToast,
}: OverviewActionPanelProps) {
  const [activeGrade, setActiveGrade] = useState<ActionGrade>(recommendedGrade)
  
  // 采纳弹窗状态
  const [acceptModal, setAcceptModal] = useState<{
    isOpen: boolean
    actionId: string
    actionContent: string
    actionGrade: 'A' | 'B' | 'C'
    timeCostDisplay?: string
  }>({ isOpen: false, actionId: '', actionContent: '', actionGrade: 'B' })
  
  // 拒绝弹窗状态
  const [rejectModal, setRejectModal] = useState<{
    isOpen: boolean
    actionId: string
    actionContent: string
    actionGrade: 'A' | 'B' | 'C'
  }>({ isOpen: false, actionId: '', actionContent: '', actionGrade: 'B' })
  
  // 加载状态
  const [actionLoadingIds] = useState<Set<string>>(new Set())
  
  const currentActions = actionsByGrade[activeGrade]
  const gradeConfig = GRADE_CONFIG[activeGrade]
  
  // 打开采纳弹窗
  const handleOpenAcceptModal = useCallback((action: Action) => {
    setAcceptModal({
      isOpen: true,
      actionId: action.id!,
      actionContent: action.content,
      actionGrade: action.actionGrade as 'A' | 'B' | 'C',
      timeCostDisplay: action.timeCostDisplay
    })
  }, [])
  
  // 确认采纳
  const handleConfirmAccept = useCallback(async (scheduledDate?: string) => {
    if (!onAcceptAction) return
    await onAcceptAction(acceptModal.actionId, scheduledDate)
    onShowToast?.('动作已采纳，已加入待办事项', 'success')
    onRefresh?.()
  }, [acceptModal.actionId, onAcceptAction, onShowToast, onRefresh])
  
  // 打开拒绝弹窗
  const handleOpenRejectModal = useCallback((action: Action) => {
    setRejectModal({
      isOpen: true,
      actionId: action.id!,
      actionContent: action.content,
      actionGrade: action.actionGrade as 'A' | 'B' | 'C'
    })
  }, [])
  
  // 确认拒绝
  const handleConfirmReject = useCallback(async (reason?: string) => {
    if (!onRejectAction) return
    await onRejectAction(rejectModal.actionId, reason)
    onShowToast?.('动作已拒绝', 'info')
    onRefresh?.()
  }, [rejectModal.actionId, onRejectAction, onShowToast, onRefresh])
  
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-6 animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-32 mb-4" />
        <div className="flex gap-2 mb-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-10 bg-gray-200 rounded-lg flex-1" />
          ))}
        </div>
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* 头部 */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">📋</span>
          <h3 className="font-semibold text-gray-900">建议动作</h3>
          {diagnosisType && (
            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
              {diagnosisType}
            </span>
          )}
        </div>
        <button
          onClick={onViewDetails}
          className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          查看全部
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* 推荐 C 级止血动作时的醒目提示（重构新增） */}
      {recommendedGrade === 'C' && (
        <div className="mx-5 mt-3 px-4 py-3 bg-orange-100 border border-orange-300 rounded-lg">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-600" />
            <span className="text-sm font-medium text-orange-800">
              检测到假设验证缺口，建议优先执行止血动作
            </span>
          </div>
        </div>
      )}

      {/* 等级切换 */}
      <div className="px-5 py-3 bg-gray-50 flex gap-2">
        {(['A', 'B', 'C'] as ActionGrade[]).map((grade) => {
          const config = GRADE_CONFIG[grade]
          const GradeIcon = config.icon
          const isActive = activeGrade === grade
          const isRecommended = recommendedGrade === grade
          
          return (
            <button
              key={grade}
              onClick={() => setActiveGrade(grade)}
              className={clsx(
                'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                isActive
                  ? `${config.bgColor} ${config.textColor} border-2 ${config.borderColor}`
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
              )}
            >
              <GradeIcon className="w-4 h-4" />
              <span>{grade}级</span>
              {isRecommended && (
                <span className="text-xs px-1.5 py-0.5 bg-white/50 rounded">推荐</span>
              )}
            </button>
          )
        })}
      </div>

      {/* 动作列表（简化版，最多显示2个） */}
      <div className="p-5 space-y-3">
        {/* 等级描述 */}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <gradeConfig.icon className={clsx('w-4 h-4', gradeConfig.color)} />
          <span>{gradeConfig.label}</span>
          {currentActions?.timeCostDays && (
            <>
              <span className="text-gray-300">·</span>
              <Clock className="w-3.5 h-3.5" />
              <span>预计 {currentActions.timeCostDays > 1 ? `${currentActions.timeCostDays}天` : `${currentActions.timeCostDays * 24}小时`}</span>
            </>
          )}
        </div>

        {/* 动作卡片 */}
        {currentActions?.actions?.slice(0, 2).map((action, index) => {
          const effectiveStatus = action.status || 'proposed'
          const isProposed = effectiveStatus === 'proposed'
          const canOperate = ['proposed', 'accepted'].includes(effectiveStatus)
          const statusConfig = ACTION_STATUS_CONFIG[effectiveStatus]
          
          return (
            <motion.div
              key={action.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={clsx(
                'p-4 rounded-lg border relative',
                gradeConfig.bgColor,
                gradeConfig.borderColor.replace('border-', 'border-')
              )}
            >
              {/* 状态标识 */}
              {effectiveStatus !== 'proposed' && (
                <div className="absolute top-2 right-2">
                  <span className={clsx(
                    'text-xs font-medium px-2 py-0.5 rounded-full',
                    statusConfig?.bgColor || 'bg-gray-100',
                    statusConfig?.color || 'text-gray-600'
                  )}>
                    {statusConfig?.label || effectiveStatus}
                  </span>
                </div>
              )}
              
              <div className="flex items-start gap-3">
                <span className="w-6 h-6 flex items-center justify-center bg-white rounded-full text-sm font-medium text-gray-600 flex-shrink-0">
                  {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={clsx(
                    'text-sm leading-relaxed',
                    effectiveStatus === 'rejected' || effectiveStatus === 'not_applicable'
                      ? 'text-gray-400 line-through'
                      : 'text-gray-800'
                  )}>
                    {action.content}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    {action.timeCostDisplay && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {action.timeCostDisplay}
                      </span>
                    )}
                    
                    {/* 操作按钮 */}
                    {onAcceptAction && onRejectAction && (
                      <div className="flex items-center gap-1.5">
                        {/* 采纳按钮 */}
                        <button
                          onClick={() => handleOpenAcceptModal(action as Action)}
                          disabled={actionLoadingIds.has(action.id!) || !isProposed}
                          className={clsx(
                            'flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors',
                            isProposed
                              ? 'text-green-700 bg-green-100 hover:bg-green-200'
                              : 'text-gray-400 bg-gray-100 cursor-not-allowed',
                            'disabled:opacity-50'
                          )}
                          title={!isProposed ? `当前状态：${statusConfig?.label}` : '采纳动作'}
                        >
                          <Check className="w-3 h-3" />
                          采纳
                        </button>
                        
                        {/* 拒绝按钮 */}
                        <button
                          onClick={() => handleOpenRejectModal(action as Action)}
                          disabled={actionLoadingIds.has(action.id!) || !canOperate}
                          className={clsx(
                            'flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors',
                            canOperate
                              ? 'text-red-700 bg-red-100 hover:bg-red-200'
                              : 'text-gray-400 bg-gray-100 cursor-not-allowed',
                            'disabled:opacity-50'
                          )}
                          title={!canOperate ? `当前状态：${statusConfig?.label}` : '拒绝动作'}
                        >
                          <X className="w-3 h-3" />
                          拒绝
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )
        })}

        {/* C级警告 */}
        {activeGrade === 'C' && (
          <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm">
            <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
            <span className="text-orange-700">
              止血动作仅用于紧急情况，建议优先考虑 B 级验证动作
            </span>
          </div>
        )}

        {/* 更多动作提示 */}
        {currentActions?.actions && currentActions.actions.length > 2 && (
          <button
            onClick={onViewDetails}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          >
            还有 {currentActions.actions.length - 2} 个动作，点击查看全部
          </button>
        )}

        {/* 无动作 */}
        {(!currentActions?.actions || currentActions.actions.length === 0) && (
          <div className="text-center py-6 text-gray-400">
            <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">暂无 {gradeConfig.label} 动作建议</p>
          </div>
        )}
      </div>
      
      {/* 采纳确认弹窗 */}
      <ActionAcceptModal
        isOpen={acceptModal.isOpen}
        onClose={() => setAcceptModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={handleConfirmAccept}
        actionContent={acceptModal.actionContent}
        actionGrade={acceptModal.actionGrade}
        timeCostDisplay={acceptModal.timeCostDisplay}
      />
      
      {/* 拒绝确认弹窗 */}
      <ActionRejectModal
        isOpen={rejectModal.isOpen}
        onClose={() => setRejectModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={handleConfirmReject}
        actionContent={rejectModal.actionContent}
        actionGrade={rejectModal.actionGrade}
      />
    </div>
  )
}
